import { useMemo } from 'react';

import { Text } from '@kanda-libs/ks-design-library';

import { JobQuoteVariant } from 'components/JobQuote';
import { formatToCurrency } from 'utils';

export interface JobQuotePricesDepositProps {
  subTotal: number;
  vatTotal: number;
  jobTotal: number;
  depositAmount: number;
  totalToPay: number;
  variant: JobQuoteVariant;
  isLoading: boolean;
}

const JobQuotePricesDeposit: React.FunctionComponent<JobQuotePricesDepositProps> =
  function ({
    subTotal,
    vatTotal,
    jobTotal,
    depositAmount,
    totalToPay,
    variant,
    isLoading,
  }) {
    const minorValueTextClassName = useMemo(
      () =>
        [
          'text-16-24-em text-neutral-900 text-right my-auto',
          variant !== 'full' ? '' : 'md:text-18-22-em',
        ].join(' '),
      [variant],
    );

    const majorValueTextClassName = useMemo(
      () =>
        [
          'text-18-22-em text-neutral-900 my-auto text-right',
          variant !== 'full' ? '' : 'md:text-20-24-em',
        ].join(' '),
      [variant],
    );

    const minorHeadingClassName = useMemo(
      () =>
        [
          'text-12-18 text-neutral-600',
          variant !== 'full' ? '' : 'md:text-14-22',
        ].join(' '),
      [variant],
    );

    const majorHeadingClassName = useMemo(
      () =>
        [
          'text-14-22-em text-neutral-900',
          variant !== 'full' ? '' : 'md:text-16-24-em',
        ].join(' '),
      [variant],
    );

    return (
      <>
        <Text
          className="text-14-22 text-neutral-600"
          text="Subtotal"
          isLoading={isLoading}
          skeletonProps={{
            width: 100,
          }}
        />
        <Text
          className="text-14-22 text-neutral-900 text-right"
          text={formatToCurrency(subTotal)}
          isLoading={isLoading}
          skeletonProps={{
            width: 100,
          }}
        />
        <Text
          className="text-14-22 text-neutral-600"
          text="VAT"
          isLoading={isLoading}
          skeletonProps={{
            width: 100,
          }}
        />
        <Text
          className="text-14-22 text-neutral-900 text-right"
          text={formatToCurrency(vatTotal)}
          isLoading={isLoading}
          skeletonProps={{
            width: 100,
          }}
        />
        <Text
          className="text-14-22-em text-neutral-900"
          text="Job Total"
          isLoading={isLoading}
          skeletonProps={{
            width: 100,
          }}
        />
        <Text
          className={minorValueTextClassName}
          text={formatToCurrency(jobTotal)}
          isLoading={isLoading}
          skeletonProps={{
            width: 100,
          }}
        />
        <div className="col-span-2 border-b border-neutral-300" />
        <div className="flex flex-col">
          <Text
            className={majorHeadingClassName}
            text="Deposit"
            isLoading={isLoading}
            skeletonProps={{
              width: 100,
            }}
          />
          <Text
            className={minorHeadingClassName}
            text="Paid to tradesperson"
            isLoading={isLoading}
            skeletonProps={{
              width: 100,
            }}
          />
        </div>
        <Text
          className={minorValueTextClassName}
          text={`-${formatToCurrency(depositAmount)}`}
          isLoading={isLoading}
          skeletonProps={{
            width: 100,
          }}
        />
        <div className="col-span-2 border-b border-neutral-300" />
        <div className="flex flex-col">
          <Text
            className={majorHeadingClassName}
            text="Outstanding To Pay"
            isLoading={isLoading}
            skeletonProps={{
              width: 100,
            }}
          />
          <Text
            className={minorHeadingClassName}
            text="In full or with finance"
            isLoading={isLoading}
            skeletonProps={{
              width: 100,
            }}
          />
        </div>
        <Text
          className={majorValueTextClassName}
          text={formatToCurrency(totalToPay)}
          isLoading={isLoading}
          skeletonProps={{
            width: 100,
          }}
        />
      </>
    );
  };

export default JobQuotePricesDeposit;
